import styles from "./style.module.scss";

import { useAppSelector } from "@/store/hooks";
import DynamicForm, { DynamicFormRef, SELECT_INPUT_TYPE } from "@mavichpe/dynamic-forms";
import ProviderStatusFormConfig from "./form";
import { HiddenableContainer } from "@components/layout/HiddenableContainer";
import { forwardRef, ReactNode, useEffect, useImperativeHandle, useRef, useState } from "react";
import { Button, Col, Row } from "reactstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faPrint } from "@fortawesome/free-solid-svg-icons";
import { ProviderApi, ProviderDataType } from "@/api/ProviderApi";
import LoadingScreen from "@components/content/LoadingScreen";
import { showAlert } from "@utils/Alert";
import DocumentStatusList, { DocumentsEntryType } from "../DocumentStatusList";
import moment from "moment";
import { ProviderStatusFormData } from "@/formTypes/ProviderStatusFormDataType";
import { loadProviderStatusData } from "@utils/DocumentStatus";
import SendDocumentToEmailOffCanvas from "@components/layout/Offcanvas/SendDocumentToEmailOffCanvas";
import { STATUS_FILTER } from "@utils/Constants";
import { safeEncodeBase64 } from "@utils/Utils";

type ProviderStatusFormProps = {
  useToggle?: boolean,
  hideAdditionalActions?: boolean,
  preventMultipleCurrency?: boolean,
  disableProjectedDate?: boolean,
  forceProviderSelect?: boolean,
  fieldTitle?: string,
  filters?: (keyof typeof STATUS_FILTER)[],
  onDocumentToogle?: (documents: DocumentsEntryType[], interestExemptedDocuments: string[]) => void,
  onShowResult?: () => void,
  children?: ReactNode,
}

const ProviderStatusForm = forwardRef<DynamicFormRef, ProviderStatusFormProps>(
  (props, ref) => {
    const { useToggle = false, forceProviderSelect = false, disableProjectedDate = false,
      preventMultipleCurrency = false, filters = [], hideAdditionalActions = false,
      onShowResult, onDocumentToogle, children } = props
    const [projectedData, setProjectedData] = useState<string>("");
    const [documents, setDocuments] = useState<DocumentsEntryType[]>([]);
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [showResults, setShowResults] = useState<boolean>(false);
    const [providers, setProviders] = useState<ProviderDataType[]>([]);
    const [providerStatusProps, setProviderStatusProps] = useState<ProviderStatusFormData>({} as ProviderStatusFormData);

    const formRef = useRef<DynamicFormRef>(null);
    useImperativeHandle(ref, () => formRef.current as DynamicFormRef, []);

    useEffect(() => {
      const today = moment();
      const formData: ProviderStatusFormData = {
        kind: "pendientes",
        endDate: today.format("YYYY-MM-DD")
      }
      formRef.current?.setFormValue("multiple", formData, "multipleData")
      loadProviders()
    }, [])

    const loadProviders = async () => {
      setIsLoading(true)
      try {
        const { items } = await ProviderApi.fetchAll();
        setProviders(items);
      } finally {
        setIsLoading(false)
      }
    }

    const reFetchData = () => {
      formRef.current?.triggerFormSubmit()
    }

    const pickStatusFilter = (keys: string[]) => {
      return keys.reduce((acc, key) => {
        if (STATUS_FILTER.hasOwnProperty(key)) {
          acc[key] = STATUS_FILTER[key as keyof typeof STATUS_FILTER];
        }
        return acc;
      }, {} as Record<string, string>);
    };

    const getFormConfig = () => {
      const endDateInput = ProviderStatusFormConfig.sections.flatMap(s => s.fields).flat().find(f => f.name === "endDate")
      if (endDateInput) {
        if (disableProjectedDate) {
          endDateInput.type = "hidden"
        } else {
          endDateInput.type = "date"
        }
      }

      const providerIdInput = ProviderStatusFormConfig.sections.flatMap(s => s.fields).flat().find(f => f.name === "providerId")
      if (forceProviderSelect) {

        if (providerIdInput) {
          providerIdInput.optionsFunction = async () => {
            const providers = await ProviderApi.optionsList();
            return providers
          };
        }
      } else {
        if (providerIdInput) {
          providerIdInput.optionsFunction = async () => {
            const providers = await ProviderApi.optionsList();
            return {
              "-2": "Todos los Provedores",
              ...providers
            }
          };
        }
      }
      const kindInput = ProviderStatusFormConfig.sections.flatMap(s => s.fields).flat().find(f => f.name === "kind")
      if (kindInput) {
        if (filters.length > 0) {
          const filterOptions = pickStatusFilter(filters);
          kindInput.options = filterOptions
        } else {
          kindInput.options = STATUS_FILTER
        }
      }

      return ProviderStatusFormConfig
    }

    const generateProviderStatus = async (formData: ProviderStatusFormData) => {
      setIsLoading(true)
      try {
        setProjectedData(formData.endDate)
        setProviderStatusProps(formData)
        const selectProvider = providers.find(p => p.id == formData.providerId);
        const plazo = selectProvider?.plazo || 0;
        const statusData = await loadProviderStatusData({
          ...formData,
          plazo
        })

        setDocuments(statusData);
        setShowResults(true)
        if (onShowResult) {
          onShowResult()
        }
      } catch (err) {
        if (err instanceof Error) {
          const errorMessage = err.message || "No fue posible generar el estado de cuenta";
          showAlert("danger", errorMessage)
        }
      }
      finally {
        setIsLoading(false)
      }
    }

    const printProviderStatus = () => {
      const queryProps = safeEncodeBase64(JSON.stringify(providerStatusProps))
      if (providerStatusProps) {
        window.open(`/user/providers/printStatus/${queryProps}`)
      }
    }

    return (
      <div className="mt-4">
        <DynamicForm<ProviderStatusFormData>
          ref={formRef}
          id="statusForm"
          formConfig={getFormConfig()}
          onSubmit={generateProviderStatus}
        >
        </DynamicForm>
        <Row>
          <Col
            xs={{
              offset: 1,
              size: 10
            }}
            sm={{
              offset: 4,
              size: 7
            }} >
            <div className={styles.buttonGroup}>
              <Button size="lg" color="primary" type="submit" form="statusForm" data-testid="load-provider-status">
                Cargar
              </Button>
              <Button hidden={!showResults || hideAdditionalActions} size="lg" color="success" type="button" onClick={printProviderStatus}>
                <FontAwesomeIcon className={styles.buttonIcon} icon={faPrint} />
                Imprimir
              </Button>
            </div>
          </Col>
        </Row>

        <HiddenableContainer show={showResults && !isLoading}>
          {children}
          <DocumentStatusList
            preventMultipleCurrency={preventMultipleCurrency}
            useToggle={useToggle}
            projectData={projectedData}
            rows={documents}
            onDocumentUpdate={reFetchData}
            onDocumentToogle={onDocumentToogle}
          />

        </HiddenableContainer>

        <SendDocumentToEmailOffCanvas
          title="Envio de Estado de Cuenta a Email" />

        <LoadingScreen isLoading={isLoading} />
      </div>
    )
  }
);

export default ProviderStatusForm;